import React from 'react';
import { BLOG_POSTS } from '../blogData';
import { ArrowLeft, Calendar, User, Share2, Facebook, Instagram, Twitter, Clock } from 'lucide-react';

interface BlogPostPageProps {
  params?: { slug?: string };
  onNavigate: (view: any, params?: any) => void;
}

export default function BlogPostPage({ params, onNavigate }: BlogPostPageProps) {
  const post = BLOG_POSTS.find(p => p.slug === params?.slug) || BLOG_POSTS[0];

  if (!post) {
    return (
      <div className="pt-40 pb-20 text-center bg-stone-50 min-h-screen">
        <h1 className="font-serif text-3xl text-mier">Journal Entry Not Found</h1>
        <button 
          onClick={() => onNavigate('blog')}
          className="mt-8 text-mier-light flex items-center gap-2 mx-auto uppercase tracking-widest text-xs font-bold"
        >
          <ArrowLeft size={16} /> Return to Journal
        </button>
      </div>
    );
  }

  return (
    <div className="bg-stone-50 min-h-screen pt-40 pb-32">
      <div className="max-w-4xl mx-auto px-6">
        {/* Breadcrumbs (SEO Optimized) */}
        <nav aria-label="Breadcrumb" className="mb-12 fade-in-up">
          <ol className="flex items-center gap-3 text-[9px] uppercase tracking-[0.2em] font-bold">
            <li>
              <button onClick={() => onNavigate('home')} className="text-mier/40 hover:text-mier transition-colors whitespace-nowrap">Home</button>
            </li>
            <li className="text-mier/20">/</li>
            <li>
              <button onClick={() => onNavigate('blog')} className="text-mier/40 hover:text-mier transition-colors whitespace-nowrap">Journal</button>
            </li>
            <li className="text-mier/20">/</li>
            <li>
              <span className="text-mier line-clamp-1" aria-current="page">{post.title}</span>
            </li>
          </ol>
        </nav>

        <button 
          onClick={() => onNavigate('blog')}
          className="group mb-16 text-mier-light hover:text-mier flex items-center gap-3 transition-all uppercase tracking-[0.2em] text-[10px] font-bold"
        >
          <ArrowLeft size={14} className="group-hover:-translate-x-2 transition-transform" /> 
          Back to Journal
        </button>

        <article>
          <header className="mb-20">
            <div className="flex items-center gap-4 text-mier/40 font-bold text-[10px] uppercase tracking-[0.3em] mb-10">
              <span className="bg-mier/5 px-4 py-1.5 rounded-full text-mier">{post.category}</span>
              <span className="flex items-center gap-2"><Clock size={12} /> 5 min read</span>
            </div>
            
            <h1 className="font-serif text-5xl md:text-7xl text-mier font-light mb-12 leading-[1.1] tracking-tight">
              {post.title}
            </h1>

            <div className="flex flex-wrap items-center justify-between gap-10 border-y border-mier/10 py-10">
              <div className="flex items-center gap-5">
                <div className="w-14 h-14 bg-mier/5 rounded-full flex items-center justify-center text-mier font-serif text-xl border border-mier/10">
                  {post.author.charAt(0)}
                </div>
                <div>
                  <div className="text-mier font-medium tracking-wide">{post.author}</div>
                  <div className="text-mier-light text-[10px] uppercase tracking-widest flex items-center gap-2 mt-1">
                    <Calendar size={12} /> {post.date}
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-6">
                <span className="text-[10px] uppercase tracking-widest text-mier/40 font-bold flex items-center gap-2">
                  <Share2 size={14} /> Share
                </span>
                <div className="flex gap-4">
                  <Facebook size={18} className="cursor-pointer text-mier/30 hover:text-mier transition-colors" />
                  <Instagram size={18} className="cursor-pointer text-mier/30 hover:text-mier transition-colors" />
                  <Twitter size={18} className="cursor-pointer text-mier/30 hover:text-mier transition-colors" />
                </div>
              </div>
            </div>
          </header>

          <div className="aspect-[21/9] mb-20 rounded-3xl overflow-hidden shadow-2xl border border-mier/5">
            <img 
              src={post.image} 
              alt={post.title}
              className="w-full h-full object-cover"
            />
          </div>

          <div 
            className="prose prose-stone max-w-none 
              prose-h2:font-serif prose-h2:text-4xl prose-h2:font-light prose-h2:text-mier prose-h2:mt-20 prose-h2:mb-10
              prose-p:text-zinc-600 prose-p:text-xl prose-p:font-light prose-p:leading-[1.8] prose-p:mb-10
              prose-li:text-zinc-600 prose-li:text-xl prose-li:font-light prose-ul:mb-10
              prose-strong:text-mier prose-strong:font-medium
              prose-blockquote:border-l-mier prose-blockquote:text-mier prose-blockquote:italic prose-blockquote:bg-mier/5 prose-blockquote:p-8 prose-blockquote:rounded-r-2xl"
            dangerouslySetInnerHTML={{ __html: post.content }}
          />

          <footer className="mt-32">
            <div className="bg-white p-12 md:p-16 rounded-3xl border border-mier/10 flex flex-col md:flex-row gap-12 items-center shadow-sm relative overflow-hidden">
              <div className="absolute top-0 left-0 w-2 h-full bg-mier/10"></div>
              <div className="w-28 h-28 bg-stone-50 rounded-full flex-shrink-0 flex items-center justify-center text-4xl font-serif text-mier border border-mier/5 shadow-inner">
                {post.author.charAt(0)}
              </div>
              <div className="text-center md:text-left flex-1">
                <h3 className="font-serif text-2xl text-mier mb-4">About the Author</h3>
                <p className="text-zinc-500 font-light leading-relaxed mb-8 text-lg">
                  {post.author} is a master artisan at MIER Beauty Lounge, specializing in luxury transformations 
                  and refined aesthetic practices tailored for the sophisticated Abu Dhabi lifestyle.
                </p>
                <button 
                  onClick={() => onNavigate('booking')}
                  className="inline-flex items-center gap-3 bg-mier text-stone-50 px-8 py-4 rounded-sm text-[10px] uppercase tracking-[0.2em] font-bold hover:bg-mier-dark transition-all shadow-lg"
                >
                  Book with {post.author.split(',')[0]}
                </button>
              </div>
            </div>
            
            <div className="mt-20 text-center">
               <button 
                onClick={() => onNavigate('blog')}
                className="text-mier-light hover:text-mier transition-colors uppercase tracking-[0.3em] text-[10px] font-bold border-b border-mier/10 pb-2"
              >
                View More Journal Entries
              </button>
            </div>
          </footer>
        </article>
      </div>
    </div>
  );
}
